import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, Sparkles } from "lucide-react";

// Rémunération FIXE selon le type de parrain — doit rester aligné avec le backend
const RATE_PARTICULIER = 25;
const RATE_PRO = 50;
const CAP_PARTICULIER = 10; // plafond mensuel de filleuls rémunérés (particuliers)

export function estimateEarnings(count, type = "particulier") {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const isPro = type === "professionnel";
  const rate = isPro ? RATE_PRO : RATE_PARTICULIER;
  const cap = isPro ? null : CAP_PARTICULIER;
  const eligible = cap != null ? Math.min(n, cap) : n;
  return {
    count: n,
    type: isPro ? "professionnel" : "particulier",
    rate,
    cap,
    eligible,
    capped: cap != null && n > cap,
    total: eligible * rate,
  };
}

const euro = (n) => `${n.toLocaleString("fr-FR")} €`;

export function CommissionCalculator({ showCta = true }) {
  const navigate = useNavigate();
  const [type, setType] = useState("particulier");
  const [count, setCount] = useState(8);
  const result = useMemo(() => estimateEarnings(count, type), [count, type]);
  const maxSlider = type === "professionnel" ? 40 : 15;

  return (
    <div className="rounded-3xl bg-white p-6 shadow-2xl sm:p-8" data-testid="commission-calculator">
      <div className="flex items-center gap-2 text-brand">
        <Calculator className="h-6 w-6" />
        <h3 className="text-lg font-extrabold text-neutral-900">Calculateur de gains</h3>
      </div>
      <p className="mt-1 text-sm text-neutral-500">Combien pouvez-vous gagner avec Plan Permis ?</p>

      {/* Type de parrain */}
      <div className="mt-5 grid grid-cols-2 gap-2 rounded-2xl bg-neutral-100 p-1" data-testid="calc-type-toggle">
        {[
          { v: "particulier", label: "Particulier", sub: "25 € / filleul" },
          { v: "professionnel", label: "Professionnel", sub: "50 € / filleul" },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            data-testid={`calc-type-${o.v}`}
            onClick={() => setType(o.v)}
            className={`rounded-xl px-3 py-2.5 text-center transition-colors ${
              type === o.v ? "bg-white shadow-sm" : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <span className={`block text-sm font-bold ${type === o.v ? "text-brand" : "text-neutral-700"}`}>
              {o.label}
            </span>
            <span className="block text-xs text-neutral-400">{o.sub}</span>
          </button>
        ))}
      </div>

      <label className="mt-6 block text-sm font-semibold text-neutral-700">
        Combien de filleuls pensez-vous apporter ce mois-ci ?
      </label>
      <div className="mt-3 flex items-center gap-4">
        <input
          type="range"
          min="0"
          max={maxSlider}
          value={Math.min(count, maxSlider)}
          onChange={(e) => setCount(Number(e.target.value))}
          className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-neutral-200 accent-brand"
          data-testid="calc-range"
        />
        <input
          type="number"
          min="0"
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="w-20 rounded-lg border border-neutral-300 px-3 py-2 text-center text-lg font-bold outline-none focus:border-brand"
          data-testid="calc-input"
        />
      </div>

      <div className="mt-6 rounded-2xl bg-neutral-50 p-5" data-testid="calc-result">
        <div className="flex items-center justify-between text-sm text-neutral-600">
          <span>
            {result.eligible} filleul{result.eligible > 1 ? "s" : ""} × {result.rate} €
          </span>
          <span className="font-semibold text-neutral-800">{euro(result.total)}</span>
        </div>
        {result.capped && (
          <p className="mt-2 text-xs font-medium text-amber-600" data-testid="calc-cap-note">
            Plafond de {result.cap} filleuls rémunérés par mois pour un particulier.
          </p>
        )}
        <div className="mt-4 border-t border-neutral-200 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Vous pourriez gagner jusqu'à
          </p>
          <p className="mt-1 text-4xl font-black text-brand" data-testid="calc-total">
            {euro(result.total)}
          </p>
          <p className="mt-1 text-xs text-neutral-400">par mois</p>
        </div>
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-xs text-neutral-400">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Estimation de gains potentiels, non contractuelle. La rémunération n'intervient qu'après inscription
        effective de chaque filleul dans une auto-école partenaire.
      </p>

      {showCta && (
        <button
          onClick={() => navigate("/ambassadeur")}
          data-testid="calc-cta-btn"
          className="mt-5 w-full rounded-full bg-brand px-8 py-4 text-base font-bold text-white transition-colors hover:bg-brand-hover"
        >
          Devenir ambassadeur
        </button>
      )}
    </div>
  );
}

export default CommissionCalculator;
